import * as pdfjs from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Build a NEW PDF containing only the page numbers the user requested. This
// lets us read only the relevant 20 pages of a 500-page planset, instead of
// uploading the whole document to Gemini (saves time and per-page billing).
// Returns the original file when the page spec is blank or "AUTO" (full file).
// Pages outside [1..N] are silently ignored.
export async function extractPdfSubset(file: File, spec: string): Promise<{ file: File; pagesKept: number; totalPages: number }> {
  const trimmed = (spec ?? '').trim();
  const arrayBuffer = await file.arrayBuffer();
  const pdfLib = await import('pdf-lib');
  const src = await pdfLib.PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
  const totalPages = src.getPageCount();
  // No spec, or "AUTO" → keep entire file (caller uploads the original).
  if (!trimmed || /^auto$/i.test(trimmed)) {
    return { file, pagesKept: totalPages, totalPages };
  }
  const wanted = parsePageList(trimmed, totalPages);
  if (!wanted.length) {
    return { file, pagesKept: totalPages, totalPages };
  }
  const out = await pdfLib.PDFDocument.create();
  const copied = await out.copyPages(src, wanted.map((n) => n - 1));
  copied.forEach((p) => out.addPage(p));
  const bytes = await out.save();
  // Preserve filename so the document record is clear.
  const subset = new File([bytes as unknown as ArrayBuffer], file.name.replace(/\.pdf$/i, '') + `_pp${wanted[0]}-${wanted[wanted.length - 1]}.pdf`, { type: 'application/pdf' });
  return { file: subset, pagesKept: wanted.length, totalPages };
}

// Parse a page spec ("3", "1, 12", "100-120, 200") into 1-based page numbers.
function parsePageList(spec: string, max: number): number[] {
  const out = new Set<number>();
  for (const tok of spec.split(',').map((t) => t.trim()).filter(Boolean)) {
    const m = tok.match(/^(\d+)\s*-\s*(\d+)$/);
    if (m) {
      const a = Math.min(+m[1]!, +m[2]!), b = Math.max(+m[1]!, +m[2]!);
      for (let i = a; i <= b; i++) if (i >= 1 && i <= max) out.add(i);
    } else if (/^\d+$/.test(tok)) {
      const n = +tok; if (n >= 1 && n <= max) out.add(n);
    }
  }
  return [...out].sort((a, b) => a - b);
}

// Plan/spec text is extracted IN THE BROWSER with pdf.js, then only the text is
// sent to the API for the AI to read. Scanned/raster sheets have no text layer —
// for those we fall back to OCR (Tesseract.js, lazy-loaded). The AI never counts.
pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export interface PageText { page: number; text: string; ocr?: boolean }
export type ExtractStage = 'text' | 'ocr';
export type ProgressFn = (done: number, total: number, stage: ExtractStage) => void;

// Pages worth analyzing in a big plan set: the legend/symbol/notes/schedule sheets
// (where the symbols live) plus the first couple of sheets (project info). This is
// why we don't just send the first N characters — the legend is rarely on page 1.
const RELEVANT_RE = /legend|symbol|abbreviation|notation|sheet\s*notes|general\s*notes|keyed?\s*notes|schedule|riser|matrix|device|table\s*of\s*contents|division\s*2[78]|section\s*2[78]|scope\s*of\s*work/i;

// Keep only the pages the estimator asked for (Step 1). Tokens (comma-separated):
//  • page numbers / ranges: "5", "100-222"
//  • a sheet label or keyword: "T" (keeps T-sheets), "Division 27", "legend"
// Returns matching pages, or ALL pages if the spec is blank or matches nothing.
export function filterPagesBySpec(pages: PageText[], spec: string): PageText[] {
  const tokens = (spec ?? '').split(',').map((t) => t.trim()).filter(Boolean);
  if (!tokens.length) return pages;
  const keep = new Set<number>();
  for (const tok of tokens) {
    const range = tok.match(/^(\d+)\s*-\s*(\d+)$/);
    if (range) {
      const a = Math.min(+range[1]!, +range[2]!), b = Math.max(+range[1]!, +range[2]!);
      for (const p of pages) if (p.page >= a && p.page <= b) keep.add(p.page);
    } else if (/^\d+$/.test(tok)) {
      const n = +tok; for (const p of pages) if (p.page === n) keep.add(p.page);
    } else {
      const re = new RegExp(tok.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
      for (const p of pages) if (re.test(p.text)) keep.add(p.page);
    }
  }
  const filtered = pages.filter((p) => keep.has(p.page));
  return filtered.length ? filtered : pages;
}

export function selectRelevantPages(pages: PageText[], maxPages = 16): PageText[] {
  if (pages.length <= maxPages) return pages;
  const picked = new Map<number, PageText>();
  for (const p of pages.slice(0, 3)) picked.set(p.page, p);          // title/index/TOC → project info + spec section list
  for (const p of pages) if (RELEVANT_RE.test(p.text)) picked.set(p.page, p);   // legend/notes/schedules
  if (picked.size < 6) {                                             // nothing matched — take an even sample
    const step = Math.max(1, Math.ceil(pages.length / 10));
    for (let i = 0; i < pages.length; i += step) { const p = pages[i]; if (p) picked.set(p.page, p); }
  }
  return [...picked.values()].sort((a, b) => a.page - b.page).slice(0, maxPages);
}

// Below this many characters a page is treated as scanned/raster and sent to OCR.
const OCR_MIN_CHARS = 24;
// Render scale for OCR — higher = better recognition, slower.
const OCR_SCALE = 2;

export async function extractPdfPages(
  file: File,
  onProgress?: ProgressFn,
  opts?: { ocr?: boolean },
): Promise<PageText[]> {
  const ocrEnabled = opts?.ocr ?? true;
  const data = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data }).promise;
  const pages: PageText[] = [];

  // A single OCR worker is created lazily on the first scanned page and reused.
  let ocr: OcrWorker | null = null;
  try {
    for (let i = 1; i <= doc.numPages; i++) {
      const page = await doc.getPage(i);
      const content = await page.getTextContent();
      let text = content.items
        .map((it) => ('str' in it ? (it as { str: string }).str : ''))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      let usedOcr = false;

      if (ocrEnabled && text.length < OCR_MIN_CHARS) {
        onProgress?.(i, doc.numPages, 'ocr');
        ocr ??= await createOcr();
        text = await ocrPage(ocr, page);
        usedOcr = true;
      } else {
        onProgress?.(i, doc.numPages, 'text');
      }

      pages.push({ page: i, text, ocr: usedOcr });
    }
  } finally {
    await ocr?.terminate();
  }
  return pages;
}

// Parse a page spec into page numbers (scanned sheets have no text, so only
// numeric ranges/numbers make sense — "3", "40-45", "3, 12").
export function parsePageNumbers(spec: string, numPages: number): number[] {
  const out: number[] = [];
  for (const tok of (spec ?? '').split(',').map((t) => t.trim()).filter(Boolean)) {
    const r = tok.match(/^(\d+)\s*-\s*(\d+)$/);
    if (r) { const a = Math.min(+r[1]!, +r[2]!), b = Math.max(+r[1]!, +r[2]!); for (let i = a; i <= b; i++) if (i >= 1 && i <= numPages) out.push(i); }
    else if (/^\d+$/.test(tok)) { const n = +tok; if (n >= 1 && n <= numPages) out.push(n); }
  }
  return [...new Set(out)];
}

// Render pages to JPEG data-URLs for multimodal reading (scanned plans/legends).
// Loads the PDF once. If the spec names pages, render those; otherwise render an
// even spread across the document so a legend somewhere in the set is still caught.
export async function renderPdfImages(file: File, spec: string, budget = 6, maxWidth = 1600): Promise<{ page: number; dataUrl: string; numPages: number }[]> {
  const data = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data }).promise;
  const N = doc.numPages;
  let nums = parsePageNumbers(spec, N);
  if (!nums.length) {
    const step = Math.max(1, Math.floor(N / budget));
    for (let p = 1; p <= N && nums.length < budget; p += step) nums.push(p);
  }
  nums = nums.slice(0, budget);
  const out: { page: number; dataUrl: string; numPages: number }[] = [];
  for (const n of nums) {
    const page = await doc.getPage(n);
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(2, Math.max(0.5, maxWidth / base.width));
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) continue;
    await page.render({ canvasContext: ctx, viewport }).promise;
    enhanceScanCanvas(canvas);   // sharpen faint B&W scans so the AI reads the legend
    out.push({ page: n, dataUrl: canvas.toDataURL('image/jpeg', 0.78), numPages: N });
  }
  return out;
}

// Conservative scan enhancement (ported from the MyKyah reference): grayscale +
// gentle contrast + light unsharp, so faint/washed-out B&W scans read better for
// the AI. Color-guarded — never grayscales a color-coded plan. Never worsens the
// page (bails untouched on any failure or on color content).
function imageDataHasColor(d: Uint8ClampedArray, stride = 41, sat = 28, frac = 0.02): boolean {
  const step = Math.max(1, Math.floor(stride)) * 4;
  let sampled = 0, colored = 0;
  for (let i = 0; i + 2 < d.length; i += step) {
    const r = d[i]!, g = d[i + 1]!, b = d[i + 2]!;
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    if (mx - mn > sat) colored++;
    sampled++;
  }
  return sampled > 0 && colored / sampled > frac;
}

function enhanceScanCanvas(canvas: HTMLCanvasElement): void {
  try {
    const ctx = canvas.getContext('2d');
    if (!ctx || !canvas.width || !canvas.height) return;
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const d = img.data;
    if (imageDataHasColor(d)) return;               // keep color plans intact
    const C = 1.25;                                 // gentle contrast around mid-gray
    for (let i = 0; i < d.length; i += 4) {
      let g = d[i]! * 0.299 + d[i + 1]! * 0.587 + d[i + 2]! * 0.114;
      g = (g - 128) * C + 128;
      g = g < 0 ? 0 : g > 255 ? 255 : g;
      d[i] = d[i + 1] = d[i + 2] = g;
    }
    const src = new Uint8ClampedArray(d);
    const row = w * 4, amount = 0.6;                // light unsharp (avoid phantom marks)
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const idx = (y * w + x) * 4;
        const c = src[idx]!;
        let s = c + amount * (4 * c - src[idx - row]! - src[idx + row]! - src[idx - 4]! - src[idx + 4]!) / 4;
        s = s < 0 ? 0 : s > 255 ? 255 : s;
        d[idx] = d[idx + 1] = d[idx + 2] = s;
      }
    }
    ctx.putImageData(img, 0, 0);
  } catch { /* leave canvas unchanged on any failure */ }
}

// ── OCR (lazy) ───────────────────────────────────────────────────────────────
interface OcrWorker { recognize(c: HTMLCanvasElement): Promise<{ data: { text: string } }>; terminate(): Promise<unknown>; }

async function createOcr(): Promise<OcrWorker> {
  const { createWorker } = await import('tesseract.js');
  return (await createWorker('eng')) as unknown as OcrWorker;
}

async function ocrPage(worker: OcrWorker, page: pdfjs.PDFPageProxy): Promise<string> {
  const viewport = page.getViewport({ scale: OCR_SCALE });
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  await page.render({ canvasContext: ctx, viewport }).promise;
  const { data } = await worker.recognize(canvas);
  return (data.text ?? '').replace(/\s+/g, ' ').trim();
}
